import {
  Card,
  CardBody,
  Flex,
  CardProps as CardPropsType,
  FormLabel,
} from '@chakra-ui/react';
import React from 'react';

import { IconButtonV2 } from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';

/**
 * A container component that wraps form inputs in a styled card with optional label and delete functionality.
 *
 * Props:
 * - `label`: Optional label displayed above the card container
 * - `children`: The form inputs or other content to display inside the container
 * - `deleteProperty`: Optional callback function that adds a delete button to remove the container
 * - `helperText`: Optional helper text displayed below the card container
 * - `FlexProps`: Optional props to pass to the root Flex component for additional styling
 * - `id`: Optional ID attribute for the container element
 * - `CardProps`: Optional, object containing props for Chakra's Card component
 * - `tag`: Optional, can pass a react component to render as a tag next to the input
 *
 * @example
 * // Basic container with single input
 * <InputCard label="Address">
 *   <TextInput name="street" label="Street" />
 * </InputCard>
 *
 * @example
 * // Container with multiple inputs and helper text
 * <InputCard
 *   label="Contact Information"
 *   helperText="Enter at least one contact method"
 * >
 *   <TextInput name="email" label="Email" />
 *   <TextInput name="phone" label="Phone" />
 * </InputCard>
 *
 * @example
 * // Deletable container for dynamic form fields
 * <InputCard
 *   label="Custom Field"
 *   deleteProperty={() => removeField(index)}
 * >
 *   <TextInput name={`fields.${index}.name`} label="Field Name" />
 *   <SelectInput name={`fields.${index}.type`} label="Field Type" options={fieldTypes} />
 * </InputCard>
 */

export type InputCardProps = {
  label?: string;
  children: React.ReactNode;
  headerChildren?: React.ReactNode;
  deleteProperty?: () => void;
  helperText?: string;
  FlexProps?: React.ComponentProps<typeof Flex>;
  CardProps?: CardPropsType;
  id?: string;
  tag?: React.ReactNode;
};

export const InputCard = ({
  label,
  children,
  headerChildren,
  deleteProperty,
  helperText,
  FlexProps,
  CardProps,
  id,
  tag,
}: InputCardProps) => (
  <Flex
    direction="column"
    gap="0.8rem"
    mb="1rem"
    _last={{ marginBottom: 0 }}
    id={id}
    {...FlexProps}
  >
    <Flex alignItems="center" justifyContent="space-between">
      {label && <FormLabel fontWeight="bold">{label}</FormLabel>}
      {tag}
      {headerChildren}
    </Flex>
    <Card variant="outline" size="md" {...CardProps}>
      <CardBody display="flex" flexDirection="row" gap="0.5rem">
        <Flex flex="1" flexDirection="column" gap="0.8rem">
          {children}
        </Flex>
        {deleteProperty && (
          <Flex>
            <IconButtonV2
              aria-label="Delete Property"
              icon={<Icon icon="trash" />}
              onClick={deleteProperty}
              variant="v2-ghost"
            />
          </Flex>
        )}
      </CardBody>
    </Card>
    {helperText && <Typography variant="detail-muted">{helperText}</Typography>}
  </Flex>
);
